import { apiRequest } from './api.js';

/** Framework-agnostic magic-link session client, shared by both SPAs. */

export const SESSION_STORAGE_KEY = 'sd.session';

export interface SessionUser {
  email: string;
  name: string;
  roles: string[];
}

export interface StoredSession {
  token: string;
  user: SessionUser;
  /** JWT `exp` (epoch seconds), 0 if it couldn't be decoded. */
  exp: number;
}

function decodeExp(jwt: string): number {
  try {
    const part = jwt.split('.')[1];
    if (!part) return 0;
    const json = atob(part.replace(/-/g, '+').replace(/_/g, '/'));
    const payload = JSON.parse(json) as { exp?: number };
    return typeof payload.exp === 'number' ? payload.exp : 0;
  } catch {
    return 0;
  }
}

export function loadSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(SESSION_STORAGE_KEY);
    if (!raw) return null;
    const s = JSON.parse(raw) as StoredSession;
    if (!s?.token || (s.exp && s.exp * 1000 <= Date.now())) {
      clearSession();
      return null;
    }
    return s;
  } catch {
    return null;
  }
}

export function saveSession(token: string, user: SessionUser): StoredSession {
  const s: StoredSession = { token, user, exp: decodeExp(token) };
  localStorage.setItem(SESSION_STORAGE_KEY, JSON.stringify(s));
  return s;
}

export function clearSession(): void {
  localStorage.removeItem(SESSION_STORAGE_KEY);
}

/** Ask the API to email a sign-in link. Always resolves (the API returns 200
 *  regardless of whether the address is authorised, to avoid enumeration).
 *
 *  `redirectUri` is this SPA's own URL (origin + base path, no trailing slash).
 *  The API builds the callback link as `${redirectUri}/auth/callback`, so an app
 *  served under a sub-path (e.g. the agent console at `/agent`) gets a link back
 *  to itself rather than to whichever SPA owns the domain root. Defaults to the
 *  origin for callers that don't run under a sub-path. */
export async function requestMagicLink(
  baseUrl: string,
  email: string,
  redirectUri: string = window.location.origin,
): Promise<{ sent: boolean; devLink?: string }> {
  return apiRequest<{ sent: boolean; devLink?: string }>(baseUrl, '/api/auth/request', {
    method: 'POST',
    body: { email, redirectUri },
  });
}

/** Exchange a magic-link token for a session and persist it. */
export async function verifyMagicToken(baseUrl: string, token: string): Promise<StoredSession> {
  const res = await apiRequest<{ token: string; user: SessionUser }>(baseUrl, '/api/auth/verify', {
    method: 'POST',
    body: { token },
  });
  return saveSession(res.token, res.user);
}
